import { Chess } from 'chess.js';
/**
 * Reading chess notation (docs/SCHAAK.md §3.6): the pure half of the notation
 * drills — random squares, a varied quiz move from a real position, plausible
 * wrong answers for it, and a sentence that says in Dutch what a move in
 * standard algebraic notation means.
 *
 * The notation taught is the **English** letters (K Q R B N), on purpose: it is
 * what Lichess, Chess.com and this app print, and what every PGN contains. The
 * Dutch letters (K D T L P) are mentioned in the lesson as what a Dutch book
 * uses.
 */
export const FILES = 'abcdefgh';
export function randomSquare(rand) {
    return `${FILES[Math.floor(rand() * 8)]}${1 + Math.floor(rand() * 8)}`;
}
/** Name the square in Dutch-friendly words: "e4 — de e-lijn, de vierde rij". */
export function describeSquare(sq) {
    const rank = Number(sq[1]);
    const ord = ['eerste', 'tweede', 'derde', 'vierde', 'vijfde', 'zesde', 'zevende', 'achtste'][rank - 1];
    return `${sq}: de ${sq[0]}-lijn, de ${ord} rij`;
}
const PIECE_WORD = { K: 'koning', Q: 'dame', R: 'toren', B: 'loper', N: 'paard' };
/** What a SAN move says, in one Dutch sentence. Unknown input comes back as itself. */
export function explainSan(san) {
    const s = san.replace(/[!?]+$/, '');
    const suffix = s.endsWith('#') ? ', mat' : s.endsWith('+') ? ', schaak' : '';
    const core = s.replace(/[+#]$/, '');
    if (core === 'O-O')
        return `korte rokade (koning naar de g-lijn, toren ernaast)${suffix}`;
    if (core === 'O-O-O')
        return `lange rokade (koning naar de c-lijn, toren ernaast)${suffix}`;
    const m = /^([KQRBN])?([a-h])?([1-8])?(x)?([a-h][1-8])(?:=([QRBN]))?$/.exec(core);
    if (!m)
        return san;
    const [, piece, fromFile, fromRank, capture, to, promo] = m;
    const who = piece ? PIECE_WORD[piece] : 'pion';
    const from = fromFile || fromRank ? ` (die van ${fromFile ?? ''}${fromRank ?? ''})` : '';
    const verb = capture ? `slaat op ${to}` : `naar ${to}`;
    const pro = promo ? ` en promoveert tot ${PIECE_WORD[promo]}` : '';
    return `${who}${from} ${verb}${pro}${suffix}`;
}
const toQuiz = (m) => ({ san: m.san, lan: m.lan, from: m.from, to: m.to });
/** How interesting a move is to read — so a drill is not twenty pawn pushes. */
function weight(m) {
    let w = 1;
    if (m.captured)
        w += 2;
    if (/[+#]/.test(m.san))
        w += 2;
    if (m.san.startsWith('O-O'))
        w += 4;
    if (m.promotion)
        w += 4;
    if (/^[KQRBN][a-h1-8]x?[a-h][1-8]/.test(m.san))
        w += 5; // disambiguated: Nbd7, R1e2
    if (m.piece !== 'p')
        w += 1;
    return w;
}
/** A legal move from `fen`, weighted towards the notation worth learning. */
export function pickQuizMove(fen, rand) {
    const moves = new Chess(fen).moves({ verbose: true });
    if (moves.length === 0)
        return null;
    const total = moves.reduce((n, m) => n + weight(m), 0);
    let r = rand() * total;
    for (const m of moves) {
        r -= weight(m);
        if (r <= 0)
            return toQuiz(m);
    }
    return toQuiz(moves[moves.length - 1]);
}
/**
 * Three wrong answers that look like the right one: the same piece's other
 * moves, and the right move with one mark wrong (a missing `x`, a `+` that is
 * not there, the wrong piece letter). Never the right answer, never a repeat.
 */
export function sanDistractors(fen, right, rand, count = 3) {
    const chess = new Chess(fen);
    const legal = chess.moves({ verbose: true });
    const piece = legal.find((m) => m.lan === right.lan)?.piece;
    const pool = new Set();
    const core = right.san.replace(/[+#]$/, '');
    const mark = right.san.slice(core.length);
    // One mark wrong: the capture sign.
    const pawnCapture = /^[a-h]x/.test(core);
    if (pawnCapture)
        pool.add(core.slice(2) + mark); // exd5 → d5
    else if (core.includes('x'))
        pool.add(core.replace('x', '') + mark); // Nxe5 → Ne5
    else if (/^[KQRBN]/.test(core))
        pool.add(core.replace(/([a-h][1-8])$/, 'x$1') + mark); // Nf3 → Nxf3
    if (mark)
        pool.add(core);
    else
        pool.add(core + '+');
    if (/^[QRBN]/.test(core)) {
        const others = ['Q', 'R', 'B', 'N'].filter((p) => p !== core[0]);
        pool.add(others[Math.floor(rand() * others.length)] + core.slice(1) + mark);
    }
    if (core === 'O-O')
        pool.add('O-O-O' + mark);
    if (core === 'O-O-O')
        pool.add('O-O' + mark);
    // The same piece going elsewhere.
    for (const m of legal)
        if (m.piece === piece && m.lan !== right.lan)
            pool.add(m.san);
    pool.delete(right.san);
    const list = shuffled([...pool], rand).slice(0, count);
    // A piece with one move (a king boxed in) leaves the pool short: fill it with
    // other pieces' moves, which are real notation too.
    if (list.length < count) {
        const rest = shuffled(legal.map((m) => m.san).filter((x) => x !== right.san && !list.includes(x)), rand);
        list.push(...rest.slice(0, count - list.length));
    }
    return list;
}
/** Shuffle the right answer in among the wrong ones. */
export function shuffled(items, rand) {
    const list = [...items];
    for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
}
/** Best streak at which a drill counts as mastered. */
export const NOTATION_MASTERY = 20;
export const NOTATION_DRILLS = [
    { key: 'find', label: 'Vind het veld', intro: 'Je ziet een veldnaam. Tik dat veld aan — zonder letters en cijfers langs het bord.' },
    { key: 'name', label: 'Noem het veld', intro: 'Eén veld licht op. Welke naam heeft het?' },
    { key: 'play', label: 'Speel de zet', intro: 'Je leest een zet. Speel hem op het bord.' },
    { key: 'read', label: 'Lees de zet', intro: 'Er wordt een zet gespeeld. Welke notatie hoort erbij?' },
];
//# sourceMappingURL=notation.js.map